import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import type { Membership, MembershipPlan } from '../api/types';
import { Alert, Spinner, formatDate, money } from '../components/common';
import { useAuth } from '../state/AuthContext';

/**
 * The membership shop window, and the member's card once they have one.
 *
 * The included drinks are collection only. That is stated plainly on every
 * plan rather than buried in terms, because a customer who subscribes expecting
 * daily delivery and discovers otherwise at checkout has been misled — and the
 * economics do not allow anything else: one delivery per drink costs more than
 * the drink.
 */
export function MembershipPage() {
  const { customer } = useAuth();
  const [plans, setPlans] = useState<MembershipPlan[] | null>(null);
  const [mine, setMine] = useState<Membership | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.membershipPlans().then(setPlans).catch((e: Error) => setError(e.message));

    if (customer) {
      api
        .membershipMine()
        .then((m) => setMine(m ?? null))
        .catch(() => setMine(null));
    } else {
      setMine(null);
    }
  }, [customer]);

  async function subscribe(plan: MembershipPlan) {
    setBusy(true);
    setError(null);

    try {
      const result = await api.membershipSubscribe(plan.code);

      if (result.paymentUrl) {
        // Straight to the gateway. The same link is emailed as well, so
        // closing this tab does not strand somebody mid-signup.
        window.location.href = result.paymentUrl;
        return;
      }

      // The gateway did not answer. The membership exists and the shop can
      // send a link by hand, so say that rather than pretending it worked.
      setMine(await api.membershipMine().then((m) => m ?? null));
      setError(
        `Membership ${result.membershipNumber} was created, but we could not open the payment page. ` +
          `Call the shop on 072 982 0176 and they will send you a link.`,
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!plans || mine === undefined) return <Spinner label="Loading memberships…" />;

  return (
    <section className="page">
      <h1>Membership</h1>
      <p className="muted">
        Drink more, pay less. Every plan includes a set number of drinks each month, collected from
        the counter at Randburg Square.
      </p>

      {error && <Alert>{error}</Alert>}

      {mine && <MembershipCard membership={mine} onChanged={setMine} />}

      {!mine?.isCurrent && (
        <>
          <h2 className="section-heading">{mine ? 'Choose a new plan' : 'Choose a plan'}</h2>

          <div className="plans">
            {plans.map((plan) => (
              <div key={plan.code} className="plan">
                <h3>{plan.name}</h3>
                <p className="muted small">{plan.tagline}</p>

                <p className="plan__price">
                  <strong>{money(plan.monthlyPrice)}</strong>
                  <span className="muted small">/month</span>
                </p>

                <p className="plan__saving">
                  {money(plan.retailValue)} of drinks — you save {money(plan.saving)}
                </p>

                <ul className="plan__benefits">
                  {plan.benefits.map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>

                {customer ? (
                  <button
                    type="button"
                    className="btn btn--primary btn--block"
                    disabled={busy}
                    onClick={() => subscribe(plan)}
                  >
                    Join {plan.name}
                  </button>
                ) : (
                  <Link to="/login" className="btn btn--primary btn--block">
                    Sign in to join
                  </Link>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      <div className="panel">
        <h2 className="panel__title">How it works</h2>
        <p className="muted small">
          <strong>Drinks are collected in store.</strong> Show your membership number at the counter
          and the drink comes off your allowance. Delivery is available as usual — you order and pay
          the normal delivery fee, because a single smoothie costs more to deliver than it does to
          make.
        </p>
        <p className="muted small">
          Unused drinks do not carry over to the next month, memberships are not transferable, and
          member discounts cannot be combined with other promotions.
        </p>
      </div>
    </section>
  );
}

function MembershipCard({
  membership,
  onChanged,
}: {
  membership: Membership;
  onChanged: (m: Membership | null) => void;
}) {
  const [busy, setBusy] = useState(false);

  async function cancel() {
    if (!window.confirm('Cancel your membership? Remaining drinks for this month are lost.')) return;

    setBusy(true);

    try {
      await api.membershipCancel('Cancelled by member');
      onChanged(await api.membershipMine().then((m) => m ?? null));
    } finally {
      setBusy(false);
    }
  }

  const used = membership.drinksUsed;
  const total = membership.drinkAllowance;
  const pct = total === 0 ? 0 : Math.round((used / total) * 100);

  return (
    <div className="memcard">
      <div className="memcard__head">
        <div>
          <span className="memcard__plan">{membership.planName}</span>
          <span className="memcard__number">{membership.membershipNumber}</span>
        </div>
        <span className={membership.isCurrent ? 'pill pill--good' : 'pill pill--bad'}>
          {membership.isCurrent ? 'Active' : membership.status}
        </span>
      </div>

      {!membership.isCurrent && membership.status === 'AwaitingPayment' && (
        <Alert tone="info">
          <strong>Not paid for yet.</strong> Your benefits start the moment it clears. Check your
          email for the payment link, or call 072 982 0176 if it has not arrived.
        </Alert>
      )}

      <div className="memcard__meter">
        <div className="memcard__bar">
          <span style={{ width: `${pct}%` }} />
        </div>
        <p>
          <strong>{membership.drinksRemaining}</strong> of {total} drinks left ·{' '}
          {membership.daysRemaining} day{membership.daysRemaining === 1 ? '' : 's'} to go
        </p>
      </div>

      {membership.blockedReason && membership.isCurrent && (
        <p className="muted small">{membership.blockedReason}</p>
      )}

      <p className="muted small">
        {membership.memberDiscountPercent}% off everything else · renews {formatDate(membership.endsAt)}
      </p>

      {membership.recent.length > 0 && (
        <details>
          <summary>Recent drinks ({membership.recent.length})</summary>
          <ul className="activity">
            {membership.recent.map((r, i) => (
              <li key={i}>
                <span>{r.itemName}</span>
                <span className="muted small">{formatDate(r.redeemedAt)}</span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {membership.isCurrent && (
        <button type="button" className="btn btn--ghost btn--sm" disabled={busy} onClick={cancel}>
          Cancel membership
        </button>
      )}
    </div>
  );
}
